import { useState, useEffect } from 'react';
import { getVideoById, VideoRecord } from '@/lib/videos';
import { transformVideoRecord } from './transform';
import { SwingLabVideo } from '../types';

// VideoWithMuxData is now just an alias since SwingLabVideo already has muxAssetId and playbackPolicy
export type VideoWithMuxData = SwingLabVideo;

export function useVideo(videoId?: string): {
  data: VideoWithMuxData | undefined;
  isLoading: boolean;
  error: Error | undefined;
} {
  const [data, setData] = useState<VideoWithMuxData | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    if (!videoId) {
      setData(undefined);
      setIsLoading(false);
      return;
    }

    async function fetchVideo() {
      try {
        setIsLoading(true);
        setError(undefined);
        
        const { data: video, error: fetchError } = await getVideoById(videoId);
        
        if (fetchError) {
          throw new Error(fetchError.message || 'Failed to fetch video');
        }
        
        if (video) {
          const videoWithMuxData = transformVideoRecord(video);
          setData(videoWithMuxData);
        } else {
          setData(undefined);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setData(undefined);
      } finally {
        setIsLoading(false);
      }
    }

    fetchVideo();
  }, [videoId]);

  return { data, isLoading, error };
}